import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { API_URL, apiFetch } from '@/config/api';

export async function parseError(res: Response): Promise<string> {
  try {
    const data = await res.json();
    return (
      (typeof data.detail === 'string' && data.detail) ||
      data?.detail?.user_error_message ||
      'Request failed'
    );
  } catch {
    return 'Request failed';
  }
}

function useInvalidateUser() {
  const queryClient = useQueryClient();
  return (userId?: number) => {
    queryClient.invalidateQueries({ queryKey: ['users'] });
    queryClient.invalidateQueries({ queryKey: ['teams'] });
    if (userId !== undefined) {
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
    } else {
      queryClient.invalidateQueries({ queryKey: ['user'] });
    }
  };
}

interface TeamMutationVars {
  teamId: string;
  userId?: number;
}

export function useCancelTeamSubscription() {
  const { getAccessTokenSilently } = useAuth0();
  const invalidateUser = useInvalidateUser();
  return useMutation({
    mutationFn: async (vars: TeamMutationVars) => {
      const token = await getAccessTokenSilently();
      const res = await apiFetch(
        `${API_URL}/admin/teams/${vars.teamId}/cancel-subscription`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!res.ok) throw new Error(await parseError(res));
      return res.json() as Promise<{
        team_id: string;
        team_name: string;
        affected_member_count: number;
        message: string;
      }>;
    },
    onSuccess: (_data, vars) => invalidateUser(vars.userId),
  });
}

export function useResumeTeamSubscription() {
  const { getAccessTokenSilently } = useAuth0();
  const invalidateUser = useInvalidateUser();
  return useMutation({
    mutationFn: async (vars: TeamMutationVars) => {
      const token = await getAccessTokenSilently();
      const res = await apiFetch(
        `${API_URL}/admin/teams/${vars.teamId}/resume-subscription`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!res.ok) throw new Error(await parseError(res));
      return res.json() as Promise<{
        team_id: string;
        team_name: string;
        message: string;
      }>;
    },
    onSuccess: (_data, vars) => invalidateUser(vars.userId),
  });
}

interface TransferTeamOwnershipVars extends TeamMutationVars {
  membershipId: string;
}

export function useTransferTeamOwnership() {
  const { getAccessTokenSilently } = useAuth0();
  const invalidateUser = useInvalidateUser();
  return useMutation({
    mutationFn: async (vars: TransferTeamOwnershipVars) => {
      const token = await getAccessTokenSilently();
      const res = await apiFetch(
        `${API_URL}/admin/teams/${vars.teamId}/transfer-ownership`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ membership_id: vars.membershipId }),
        }
      );
      if (!res.ok) throw new Error(await parseError(res));
      return res.json() as Promise<{ message: string }>;
    },
    onSuccess: (_data, vars) => invalidateUser(vars.userId),
  });
}

interface EnableTeamBaaVars extends TeamMutationVars {
  legalName: string;
}

export function useEnableTeamBaa() {
  const { getAccessTokenSilently } = useAuth0();
  const invalidateUser = useInvalidateUser();
  return useMutation({
    mutationFn: async (vars: EnableTeamBaaVars) => {
      const token = await getAccessTokenSilently();
      const res = await apiFetch(`${API_URL}/admin/teams/${vars.teamId}/baa`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ legal_name: vars.legalName }),
      });
      if (!res.ok) throw new Error(await parseError(res));
      return res.json() as Promise<{ baa_enabled: boolean }>;
    },
    onSuccess: (_data, vars) => invalidateUser(vars.userId),
  });
}

export function useDisableTeamBaa() {
  const { getAccessTokenSilently } = useAuth0();
  const invalidateUser = useInvalidateUser();
  return useMutation({
    mutationFn: async (vars: TeamMutationVars) => {
      const token = await getAccessTokenSilently();
      const res = await apiFetch(`${API_URL}/admin/teams/${vars.teamId}/baa`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await parseError(res));
      return res.json() as Promise<{ baa_enabled: boolean }>;
    },
    onSuccess: (_data, vars) => invalidateUser(vars.userId),
  });
}

export function useEnableTeamSso() {
  const { getAccessTokenSilently } = useAuth0();
  const invalidateUser = useInvalidateUser();
  return useMutation({
    mutationFn: async (vars: TeamMutationVars) => {
      const token = await getAccessTokenSilently();
      const res = await apiFetch(`${API_URL}/admin/teams/${vars.teamId}/sso`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await parseError(res));
      return res.json() as Promise<{ sso_enabled: boolean }>;
    },
    onSuccess: (_data, vars) => invalidateUser(vars.userId),
  });
}

export function useDisableTeamSso() {
  const { getAccessTokenSilently } = useAuth0();
  const invalidateUser = useInvalidateUser();
  return useMutation({
    mutationFn: async (vars: TeamMutationVars) => {
      const token = await getAccessTokenSilently();
      const res = await apiFetch(`${API_URL}/admin/teams/${vars.teamId}/sso`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await parseError(res));
      return res.json() as Promise<{ sso_enabled: boolean }>;
    },
    onSuccess: (_data, vars) => invalidateUser(vars.userId),
  });
}

interface AddTeamCreditsVars extends TeamMutationVars {
  amount_cents: number;
  description?: string;
}

export function useAddTeamCredits() {
  const { getAccessTokenSilently } = useAuth0();
  const invalidateUser = useInvalidateUser();
  return useMutation({
    mutationFn: async (vars: AddTeamCreditsVars) => {
      const token = await getAccessTokenSilently();
      const res = await apiFetch(
        `${API_URL}/admin/teams/${vars.teamId}/add-credits`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            amount_cents: vars.amount_cents,
            description: vars.description,
          }),
        }
      );
      if (!res.ok) throw new Error(await parseError(res));
      return res.json() as Promise<{
        team_id: string;
        team_name: string;
        amount_cents: number;
        message: string;
      }>;
    },
    onSuccess: (_data, vars) => invalidateUser(vars.userId),
  });
}

interface TeamUsageCreditsVars extends TeamMutationVars {
  amount_usd: number;
  reason?: string;
}

export function useAddTeamUsageCredits() {
  const { getAccessTokenSilently } = useAuth0();
  const invalidateUser = useInvalidateUser();
  return useMutation({
    mutationFn: async (vars: TeamUsageCreditsVars) => {
      const token = await getAccessTokenSilently();
      const res = await apiFetch(
        `${API_URL}/admin/teams/${vars.teamId}/credits/add-usage`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            amount_usd: vars.amount_usd,
            reason: vars.reason,
          }),
        }
      );
      if (!res.ok) throw new Error(await parseError(res));
      return res.json() as Promise<{
        balance: number;
        purchased_balance: number;
      }>;
    },
    onSuccess: (_data, vars) => invalidateUser(vars.userId),
  });
}

export function useRemoveTeamUsageCredits() {
  const { getAccessTokenSilently } = useAuth0();
  const invalidateUser = useInvalidateUser();
  return useMutation({
    mutationFn: async (vars: TeamUsageCreditsVars) => {
      const token = await getAccessTokenSilently();
      const res = await apiFetch(
        `${API_URL}/admin/teams/${vars.teamId}/credits/remove-usage`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            amount_usd: vars.amount_usd,
            reason: vars.reason,
          }),
        }
      );
      if (!res.ok) throw new Error(await parseError(res));
      return res.json() as Promise<{
        balance: number;
        purchased_balance: number;
      }>;
    },
    onSuccess: (_data, vars) => invalidateUser(vars.userId),
  });
}

interface ApplyTeamDiscountVars extends TeamMutationVars {
  coupon_id?: string;
  discount_percentage?: number;
  duration_months?: number;
}

export function useApplyTeamDiscount() {
  const { getAccessTokenSilently } = useAuth0();
  const invalidateUser = useInvalidateUser();
  return useMutation({
    mutationFn: async (vars: ApplyTeamDiscountVars) => {
      const token = await getAccessTokenSilently();
      const res = await apiFetch(
        `${API_URL}/admin/teams/${vars.teamId}/apply-discount`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            coupon_id: vars.coupon_id,
            discount_percentage: vars.discount_percentage,
            duration_months: vars.duration_months,
          }),
        }
      );
      if (!res.ok) throw new Error(await parseError(res));
      return res.json() as Promise<{
        team_id: string;
        team_name: string;
        coupon_id: string;
        coupon_code: string | null;
        message: string;
      }>;
    },
    onSuccess: (_data, vars) => invalidateUser(vars.userId),
  });
}

export function useDeleteTeam() {
  const { getAccessTokenSilently } = useAuth0();
  const invalidateUser = useInvalidateUser();
  return useMutation({
    mutationFn: async (vars: TeamMutationVars) => {
      const token = await getAccessTokenSilently();
      const res = await apiFetch(`${API_URL}/admin/teams/${vars.teamId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) throw new Error(await parseError(res));
      return res.json() as Promise<{
        team_id: string;
        team_name: string;
        affected_member_count: number;
        message: string;
      }>;
    },
    onSuccess: (_data, vars) => invalidateUser(vars.userId),
  });
}

export interface CreateTrialMemberInput {
  email: string;
  tier: 'plus' | 'power' | 'pro' | 'pro2';
  role: 'admin' | 'member';
}

export interface CreateTeamWithTrialVars {
  owner_email: string;
  team_name: string;
  owner_tier: 'plus' | 'power' | 'pro' | 'pro2';
  billing_interval: 'monthly' | 'annual';
  trial_end: string;
  members: CreateTrialMemberInput[];
}

export function useCreateTeamWithTrial() {
  const { getAccessTokenSilently } = useAuth0();
  const invalidateUser = useInvalidateUser();
  return useMutation({
    mutationFn: async (vars: CreateTeamWithTrialVars) => {
      const token = await getAccessTokenSilently();
      const res = await apiFetch(`${API_URL}/admin/teams/create-with-trial`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(vars),
      });
      if (!res.ok) throw new Error(await parseError(res));
      return res.json() as Promise<{
        team_id: string;
        owner_user_id: number;
        team_name: string;
        trial_end: string;
        seat_count: number;
        members_invited: string[];
        message: string;
      }>;
    },
    onSuccess: (data) => invalidateUser(data.owner_user_id),
  });
}
